import Link from 'components/ui/AppLink';
import Loader from 'components/ui/LoadingIndicator';
import { useTheme } from '@mui/material/styles';
import type { Theme as ExtendedTheme } from '@mui/material/styles';
import {
    Box,
    GlobalStyles,
    Grid,
    Paper,
    Stack,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { MessageView } from 'components/ui/Feedback';

import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
    CHANNEL_FIELDS_FRAGMENT,
    GET_PREMIUM_CHANNEL_BY_ID_LEAN,
} from '../../../gql/channels';
import {
    Article,
    ChannelTypes,
    PromotionalContentSummary,
    User,
} from '../../../shared/types';
import { baseUrls } from '../../../utils/baseUrls.utils';
import { getUsersWithNames } from '../../../utils/filtering.utils';
import { getLinkPrefix } from '../../../utils/list-utils';
import { promotionalContentShortcodesToHtml } from '../../../utils/promotional-content.utils';
import { useArticleActivityLogger } from '../../hooks/use-activity-logger';
import { useViewStats } from '../../hooks/use-view-stats';
import { useLogger } from '../../providers/logger/Logger';
import { LogAction, LogLevel } from '../../providers/logger/logger-types';
import { useUser } from '../../providers/UserProvider';
import ArticleContent from '../../ui/ArticleContent';
import ArticleContributorList from '../../ui/ArticleContributorList';
import ArticleHeader from '../../ui/ArticleHeader';
import { ContributorProps } from '../../ui/Contributor';
import DeskEmail from '../../ui/DeskEmail';
import Disclaimer from '../../ui/Disclaimer';
import PromotionalContentSidebar from '../../ui/PromotionalContentSidebar';
import { getPublishedData } from '../../ui/PublishedBy';
import { ChannelCoverFetchData } from './ChannelCoverFetchData';
import { ChannelSubscriptionButton } from './ChannelSubscriptionButton';
import InterestedInPremium from './InterestedInPremium';
import { isChartsArticle } from '../../../utils/tableau';
import { getChannelLink } from '../../../utils/link.utils';
import ChannelCard from './ChannelCardNew';
import LatestArticlesFromChannel from './LatestArticlesFromChannel';
import { isValidMongoId } from '../../../utils/id-validation.utils';
import { getArticlePrintBrandingTokens } from '../../../utils/print-theme.utils';

import { useLazyQuery } from '@apollo/react-hooks';
import Bowser from 'bowser';
import gql from 'graphql-tag';
import ArrowLeft from '@mds/icons/ArrowLeft';

const articlePrintGlobalStyles = (theme: ExtendedTheme) => {
    const printBranding = getArticlePrintBrandingTokens(theme);

    return {
        '@media print and (color)': {
            '*': {
                printColorAdjust: 'exact',
            },
        },
        '@media print': {
            ':root': {
                colorScheme: 'light',
            },
            html: {
                color: printBranding.textColor,
            },
            body: {
                color: printBranding.textColor,
                backgroundImage: `url(${printBranding.pageLogoSrc})`,
                backgroundSize: printBranding.pageLogoSize,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: `right ${printBranding.pageLogoOffset} top ${printBranding.pageLogoOffset}`,
            },
            '#article-page-layout, #article-page-layout *': {
                color: printBranding.textColor,
            },
            '#article-page-layout a': {
                color: printBranding.linkColor,
            },
        },
    };
};

const FIND_ARTICLE_BY_ID = gql`
	query findPublishedArticleById($id: ID!) {
		article: findPublishedArticleById(id: $id) {
			_id
			title
			html
			publishedBy { _id name type }
			contributors { _id name position phone }
			categories { _id name }
			tags { _id name }
			summary
			publishDate
			lastEdited
			state
			version
			isPremium
			isPremiumActive
			premiumExpiryOffset
			premiumChannel
			premiumChannelDetails {
				${CHANNEL_FIELDS_FRAGMENT}
				userChannelSubscription {
					subscriptionState
				}
			}
			displayInChartSection
			canSeeFullVersion
			randomisedPromotionalContent {
				_id
				name
				link
				sidebarImage
			}
			deskEmail
			isLocked
			showWatermark
			isPinned
		}
		promotionalContent {
			_id
			name
			link
			articleImage
			expiryDate
		}
	}
`;

const ARTICLE_NOT_FOUND_ERROR = 'ARTICLE_NOT_FOUND';

const ArticlePage: React.FC = () => {
    const { _id } = useParams<{ _id: string }>();
    const isValidArticleId = isValidMongoId(_id);
    const theme = useTheme();
    const showFullWidthArticleContent = useMediaQuery(
        theme.breakpoints.down('sm')
    );
    const { push } = useHistory();
    const addViewStats = useViewStats();
    const { userDetails } = useUser();
    const { printLog } = useLogger();

    const [isFixedLogoSupport, setIsFixedLogoSupport] = useState(false);
    const [article, setArticle] = useState<Article | undefined>();

    const [getChannel, { data: channelData }] = useLazyQuery(
        GET_PREMIUM_CHANNEL_BY_ID_LEAN,
        {
            variables: { id: _id },
            fetchPolicy: 'network-only',
        }
    );

    const [findArticleByIdQuery, { loading, data, error }] = useLazyQuery<{
        article: Article;
        promotionalContent: PromotionalContentSummary[];
    }>(FIND_ARTICLE_BY_ID, {
        variables: { id: _id },
        fetchPolicy: 'network-only',
    });

    useEffect(() => {
        let article!: Article;

        if (data?.article) {
            article = data.article;
            window.Appcues?.track('Article View', {
                desk: article.publishedBy.name,
                articleIsPremium: article.isPremiumActive,
                userCanViewPremium: article.canSeeFullVersion,
            });

            if (article.canSeeFullVersion) {
                addViewStats(article);
            }

            // all articles can be without Channel
            article.premiumChannel &&
                getChannel({
                    variables: {
                        id: article.premiumChannel,
                    },
                });
        }

        if (data?.article && data.promotionalContent.length) {
            article = {
                ...data.article,
                html: promotionalContentShortcodesToHtml(
                    data.article.html,
                    data.promotionalContent
                ),
            };
        }

        setArticle(article);
    }, [data]);

    useEffect(() => {
        if (isValidArticleId) {
            findArticleByIdQuery();
        }
    }, []);

    useEffect(() => {
        if (error && error.message.includes(ARTICLE_NOT_FOUND_ERROR)) {
            push(baseUrls.forYou);
        }
    }, [error]);

    const hideDisclaimer = article?.premiumChannel
        ? channelData?.channel?.hideDisclaimer
        : false;

    useArticleActivityLogger(
        'Article',
        article?.title,
        article?._id,
        data?.article?.premiumChannelDetails?.name,
        data?.article?.premiumChannelDetails?._id
    );

    useEffect(() => {
        if (article) {
            if (!article?.canSeeFullVersion && article?.isPremiumActive) {
                printLog(
                    LogLevel.INFO,
                    LogAction.ViewPremiumWithoutAccess,
                    'User attempted to view, premium article',
                    {
                        id: _id,
                        title: article?.title,
                    }
                );
            }
            if (!article?.canSeeFullVersion && !article?.isPremiumActive) {
                printLog(
                    LogLevel.INFO,
                    LogAction.ViewLockedWithoutAccess,
                    'User attempted to view article from locked category',
                    {
                        id: _id,
                        title: article?.title,
                    }
                );
            }
        }
    }, [_id, article, printLog]);

    useEffect(() => {
        const printLogger = () => {
            printLog(
                LogLevel.INFO,
                LogAction.ArticlePrinted,
                'User printed article',
                {
                    id: _id,
                    title: article?.title,
                }
            );
        };

        window.addEventListener('afterprint', printLogger);

        return () => {
            window.removeEventListener('afterprint', printLogger);
        };
    }, [_id, article, printLog]);

    useEffect(() => {
        const browser = Bowser.parse(window.navigator.userAgent);

        if (browser.platform.type !== 'desktop') {
            setIsFixedLogoSupport(false);
        } else if (browser.browser.name !== 'Safari') {
            setIsFixedLogoSupport(true);
            document.body.style.backgroundImage = 'none';
        }
    }, []);

    const errorMessage = (
        <MessageView
            styleName="primary"
            size="large"
            title="Article not found"
            primaryAction={{
                label: 'Return to Neon Insights',
                onClick: () => push('/'),
            }}
        ></MessageView>
    );

    if (!isValidArticleId) return errorMessage;

    if (loading || !article || (article.premiumChannel && !channelData))
        return <Loader />;

    const chartsArticle =
        isChartsArticle(article.html) || article.displayInChartSection;

    const publishedData = getPublishedData(
        article,
        getLinkPrefix(),
        false,
        article.version > 1
    );

    const isClosedFreeChannel =
        article.premiumChannelDetails?.type === ChannelTypes.freeClosed;

    const verticalDivider = (
        <Box
            sx={(theme) => ({
                [theme.breakpoints.up('md')]: {
                    px: 2,
                },
                [theme.breakpoints.up('lg')]: {
                    px: 6,
                },
                '@media print': {
                    display: 'none',
                },
            })}
        >
            <Box
                sx={(theme) => ({
                    [theme.breakpoints.up('md')]: {
                        width: 1,
                        height: '100%',
                        bgcolor: 'divider',
                    },
                })}
            />
        </Box>
    );

    const fullWidthContent = chartsArticle || showFullWidthArticleContent;
    const printBranding = getArticlePrintBrandingTokens(theme);

    return (
        <Grid
            container
            spacing={2}
            sx={(theme) => ({
                [theme.breakpoints.only('md')]: { mt: 1 },
                '@media print': {
                    maxWidth: '100%',
                    width: '100%',
                    margin: 0,
                },
            })}
        >
            <GlobalStyles styles={articlePrintGlobalStyles(theme)} />
            {isFixedLogoSupport && (
                <Grid
                    sx={{
                        display: 'none',
                        '@media print': { display: 'block' },
                    }}
                    size={12}
                >
                    <Box
                        component="img"
                        sx={{
                            display: 'block',
                            width: printBranding.headerLogoWidth,
                            mb: printBranding.headerLogoMarginBottom,
                            maxWidth: '100%',
                            height: 'auto',
                        }}
                        alt="Marex"
                        src={printBranding.headerLogoSrc}
                    />
                </Grid>
            )}
            {article.premiumChannelDetails ? (
                article.canSeeFullVersion ? (
                    chartsArticle ? (
                        <Link
                            to={getChannelLink(
                                article.premiumChannelDetails._id
                            )}
                            color="inherit"
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                color: 'text.primary',
                                m: (theme) => theme.spacing(0, 0, 2, 1),
                                '&:hover': {
                                    color: 'text.primary',
                                },
                            }}
                        >
                            <ArrowLeft fontSize="small" />
                            <Typography component="span" variant="label2">
                                {article.premiumChannelDetails.name}
                            </Typography>
                        </Link>
                    ) : (
                        <></>
                    )
                ) : (
                    <Grid size={12}>
                        <Box sx={{ mb: 4 }}>
                            <ChannelCoverFetchData
                                channelId={article.premiumChannel}
                            />
                        </Box>
                    </Grid>
                )
            ) : (
                <></>
            )}
            <Grid size={12}>
                <ArticleHeader
                    title={article.title}
                    summary={article.summary}
                    id={article._id}
                    publishedData={publishedData}
                    channel={article.premiumChannelDetails}
                    chartsArticle={chartsArticle}
                />
            </Grid>
            <Grid size={12}>
                <Box
                    id="article-page-layout"
                    sx={{
                        display: 'flex',
                        flexDirection: fullWidthContent ? 'column' : 'row',
                        '@media print': {
                            flexDirection: 'column',
                        },
                    }}
                >
                    <Box
                        sx={{
                            width: fullWidthContent ? '100%' : '66%',
                            // Allow a wide table to scroll within the article
                            // column instead of increasing this flex item's width.
                            minWidth: 0,
                            '@media print': {
                                width: '100%',
                            },
                        }}
                    >
                        {article.canSeeFullVersion && article.html ? (
                            <ArticleContent
                                title={article.title}
                                html={article.html}
                                id={article._id}
                                showWatermark={article.showWatermark}
                                watermarkText={userDetails?.email}
                            />
                        ) : article.premiumChannelDetails ? (
                            <Paper sx={{ pt: 8, pb: 4 }}>
                                <MessageView
                                    styleName="primary"
                                    size="medium"
                                    title={
                                        isClosedFreeChannel
                                            ? `Become a subscriber of ${article.premiumChannelDetails.name} to access this article`
                                            : `Read more from ${article.premiumChannelDetails.name}`
                                    }
                                    icon={
                                        isClosedFreeChannel ? undefined : <></>
                                    }
                                >
                                    <Box sx={{ pb: 4 }}>
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                color: 'text.secondary',
                                            }}
                                        >
                                            {isClosedFreeChannel ? (
                                                <>
                                                    Once sent, your request will
                                                    be reviewed by the channel
                                                    owners, and you will be
                                                    notified via email when your
                                                    access is granted.
                                                </>
                                            ) : (
                                                <>
                                                    You’ve read all your free
                                                    premium channel articles.
                                                    Subscribe to the channel to
                                                    get unlimited access.
                                                </>
                                            )}
                                        </Typography>
                                    </Box>
                                    <ChannelSubscriptionButton
                                        channel={article.premiumChannelDetails}
                                        disclaimer={false}
                                    />
                                </MessageView>
                            </Paper>
                        ) : (
                            <InterestedInPremium
                                articleTitle={article.title}
                                deskEmail={article.deskEmail}
                                lockedByCategory={article?.isLocked}
                            />
                        )}
                    </Box>
                    {!fullWidthContent && verticalDivider}
                    <Stack
                        spacing={6}
                        sx={{
                            width: fullWidthContent ? '100%' : '33%',
                            mt: fullWidthContent ? 4 : 0,
                            '@media print': {
                                display: 'none',
                            },
                        }}
                    >
                        {!chartsArticle &&
                            article.premiumChannelDetails &&
                            article.canSeeFullVersion && (
                                <ChannelCard
                                    name={article.premiumChannelDetails.name}
                                    owner={
                                        article.premiumChannelDetails.owner.name
                                    }
                                    url={`${baseUrls.browse.channelMain}/${article.premiumChannelDetails._id}`}
                                    image={
                                        article.premiumChannelDetails.coverImage
                                    }
                                    premium={
                                        article.premiumChannelDetails.type ===
                                        'premium'
                                    }
                                    isSubscribed={
                                        article.premiumChannelDetails
                                            .isCurrentUserSubscribed
                                    }
                                />
                            )}
                        {article.deskEmail && (
                            <DeskEmail email={article.deskEmail} />
                        )}
                        <ArticleContributorList
                            narrow={chartsArticle}
                            publishedBy={article.publishedBy}
                            contributors={(
                                getUsersWithNames(
                                    article.contributors as User[]
                                ) || []
                            ).map(
                                ({
                                    name,
                                    position,
                                    _id,
                                    phone,
                                }: Partial<User>): ContributorProps => ({
                                    _id: _id || '',
                                    name: name || '',
                                    jobFunction: position || '',
                                    phone: phone || '',
                                })
                            )}
                        />
                        {!chartsArticle && article?.premiumChannel ? (
                            <LatestArticlesFromChannel
                                channelId={article?.premiumChannel}
                                currentArticleId={article._id}
                            />
                        ) : (
                            <></>
                        )}
                        {!chartsArticle &&
                            article.randomisedPromotionalContent && (
                                <PromotionalContentSidebar article={article} />
                            )}
                    </Stack>
                </Box>
            </Grid>
            {!hideDisclaimer ? (
                <Grid sx={{ mt: 8, '@media print': { mt: 4 } }} size={12}>
                    <Disclaimer />
                </Grid>
            ) : (
                <></>
            )}
        </Grid>
    );
};

export default ArticlePage;
